/**
 * featureFlags.js — Feature flags gerenciáveis para controle de features
 * 
 * Este arquivo fornece um sistema simples de feature flags independente de backend,
 * permitindo habilitar/desabilitar funcionalidades do frontend sem deploy.
 * 
 * Para integração com backend/LaunchDarkly/ConfigCat no futuro:
 * - Substituir FEATURES por fetch de API remota
 * - Implementar cache local (localStorage) com TTL
 * - Adicionar middleware para A/B testing
 * 
 * @version 1.0.0
 * @date 2026-02-17
 */

// ════════════════════════════════════════════════════════════════════════════
// FEATURE FLAGS CONFIGURATION
// ════════════════════════════════════════════════════════════════════════════

/**
 * Feature flags locais (stub)
 * 
 * Regra: itens com status "planned" em sidebarConfig.js sempre disabled,
 * independente do valor da flag aqui.
 */
export const FEATURE_FLAGS = {
  // ──────────────────────────────────────────────────────────────────────────
  // CORE FEATURES (Ativas)
  // ──────────────────────────────────────────────────────────────────────────
  
  /**
   * Feed social (LinkedIn-style)
   * Status: BETA (ativo)
   */
  ENABLE_SOCIAL_FEED: true,

  /**
   * Sistema de notificações sociais
   * Status: ATIVO
   */
  ENABLE_NOTIFICATIONS_SOCIAL: true,

  /**
   * Chat/DMs 1:1
   * Status: ATIVO
   */
  ENABLE_CHAT: true,

  /**
   * Módulo de treinos (AthleteShell /my/training/*)
   * Status: ATIVO
   */
  ENABLE_TRAINING: true,

  /**
   * Playbook System (American Football)
   * Status: FORA DO MVP (SEC-A lote desliga 11/07/2026)
   * Backend atras de ENABLE_PLAYBOOK_API=false (fail-closed).
   * Nota: esta flag NAO tem consumidor no codigo (corte real e via
   * status 'planned' no sidebarConfig + redirect das rotas no App.js).
   * Mantida false por coerencia com o estado do backend.
   */
  ENABLE_PLAYBOOK: false,

  /**
   * Tactics Designer (Football + American Football)
   * Status: ATIVO
   */
  ENABLE_TACTICS: true,

  /**
   * Swim Vision (Computer Vision + Digital Twin - Minas partnership)
   * Status: ATIVO
   */
  ENABLE_SWIM_VISION: true,

  /**
   * Biblioteca Arena (Technical Library)
   * Status: ATIVO
   */
  ENABLE_LIBRARY: true,

  /**
   * Scouting/Recruiter tools
   * Status: ATIVO
   */
  ENABLE_RECRUITER: true,

  /**
   * Research tools (arena.researcher.read capability)
   * Status: ATIVO
   */
  ENABLE_RESEARCH: true,

  /**
   * Governance (ADMIN/FOUNDER only)
   * Status: ATIVO
   */
  ENABLE_GOVERNANCE: true,

  /**
   * Central Hub (Performance OS dashboard)
   * Status: ATIVO
   */
  ENABLE_CENTRAL: true,

  // ──────────────────────────────────────────────────────────────────────────
  // PLANNED FEATURES (Desativadas - Em desenvolvimento)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Monitoramento de carga (TRIMP, RPE, GPS)
   * Status: PLANEJADO
   */
  ENABLE_LOAD_MONITORING: false,

  /**
   * Protocolos de recuperação
   * Status: PLANEJADO
   */
  ENABLE_RECOVERY: false,

  /**
   * Periodização (macro/meso/microciclo)
   * Status: PLANEJADO
   */
  ENABLE_PERIODIZATION: false,

  /**
   * Session Builder (Editor visual de sessões - VUKZ Assist)
   * Status: PLANEJADO
   */
  ENABLE_SESSION_BUILDER: false,

  /**
   * Biblioteca de vídeos
   * Status: PLANEJADO
   */
  ENABLE_VIDEO_LIBRARY: false,

  /**
   * Tagging de vídeos (Agente Vision)
   * Status: PLANEJADO
   */
  ENABLE_VIDEO_TAGGING: false,

  /**
   * Rankings unificados multi-sport
   * Status: PLANEJADO
   */
  ENABLE_UNIFIED_RANKINGS: false,

  /**
   * Histórico de recordes
   * Status: PLANEJADO
   */
  ENABLE_RECORDS_HISTORY: false,

  /**
   * Talent Radar (descoberta automatizada - Agente Recruiter)
   * Status: PLANEJADO
   */
  ENABLE_TALENT_RADAR: false,

  /**
   * Dashboard Médico
   * Status: PLANEJADO
   */
  ENABLE_MEDICAL_DASHBOARD: false,

  /**
   * Controle de lesões
   * Status: PLANEJADO
   */
  ENABLE_INJURY_MANAGEMENT: false,

  /**
   * Reabilitação
   * Status: PLANEJADO
   */
  ENABLE_REHABILITATION: false,

  /**
   * Screenings (avaliações físicas)
   * Status: PLANEJADO
   */
  ENABLE_MEDICAL_SCREENING: false,

  /**
   * Liberações médicas
   * Status: PLANEJADO
   */
  ENABLE_MEDICAL_CLEARANCE: false,

  /**
   * Sistema de fóruns
   * Status: PLANEJADO
   */
  ENABLE_FORUMS: false,

  /**
   * Grupos privados (time, clube)
   * Status: PLANEJADO
   */
  ENABLE_PRIVATE_GROUPS: false,

  /**
   * Gestão financeira
   * Status: PLANEJADO
   */
  ENABLE_FINANCE_MANAGEMENT: false,

  /**
   * Gestão de contratos
   * Status: PLANEJADO
   */
  ENABLE_CONTRACT_MANAGEMENT: false,

  /**
   * Gestão de instalações
   * Status: PLANEJADO
   */
  ENABLE_FACILITIES_MANAGEMENT: false,

  /**
   * Auditoria (logs LGPD)
   * Status: PLANEJADO
   */
  ENABLE_AUDIT_LOGS: false,

  /**
   * Data Lineage (Purview)
   * Status: PLANEJADO
   */
  ENABLE_DATA_LINEAGE: false,

  /**
   * Testes de força (1RM, CMJ)
   * Status: PLANEJADO
   */
  ENABLE_STRENGTH_TESTING: false,

  /**
   * Testes de velocidade/agilidade
   * Status: PLANEJADO
   */
  ENABLE_SPEED_TESTING: false,

  /**
   * Condicionamento aeróbico/anaeróbico
   * Status: PLANEJADO
   */
  ENABLE_CONDITIONING: false,

  /**
   * Preferências de usuário (notifs, idioma)
   * Status: PLANEJADO
   */
  ENABLE_USER_PREFERENCES: false,

  // ──────────────────────────────────────────────────────────────────────────
  // EXPERIMENTAL/DEBUG FEATURES
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Debug mode (mostra logs detalhados no console)
   * Status: ATIVO para desenvolvimento
   */
  ENABLE_DEBUG_MODE: process.env.NODE_ENV === 'development',

  /**
   * Performance monitoring (React Profiler)
   * Status: ATIVO para desenvolvimento
   */
  ENABLE_PERFORMANCE_MONITORING: process.env.NODE_ENV === 'development',

  /**
   * Mock data mode (usa dados fake sem chamar API)
   * Status: DESATIVADO (usar apenas para testes isolados)
   */
  ENABLE_MOCK_DATA: false,
};

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Verifica se uma feature está habilitada
 * @param {string} flagName - Nome da flag (ex: 'ENABLE_SOCIAL_FEED')
 * @returns {boolean}
 */
export const isFeatureEnabled = (flagName) => {
  return Boolean(FEATURE_FLAGS[flagName]);
};

/**
 * Retorna todas as flags ativas (true)
 * @returns {string[]}
 */
export const getActiveFeatures = () => {
  return Object.keys(FEATURE_FLAGS).filter((key) => FEATURE_FLAGS[key] === true);
};

/**
 * Retorna todas as flags inativas (false)
 * @returns {string[]}
 */
export const getInactiveFeatures = () => {
  return Object.keys(FEATURE_FLAGS).filter((key) => FEATURE_FLAGS[key] === false);
};

/**
 * Override de flags (para testes)
 * NÃO USAR EM PRODUÇÃO - apenas para desenvolvimento/testes
 * @param {Object} overrides - Objeto com flags a sobrescrever
 */
export const setFeatureOverrides = (overrides) => {
  if (process.env.NODE_ENV === 'production') {
    console.warn('[featureFlags] setFeatureOverrides não deve ser usado em produção');
    return;
  }

  Object.keys(overrides).forEach((key) => {
    if (FEATURE_FLAGS.hasOwnProperty(key)) {
      FEATURE_FLAGS[key] = overrides[key];
      console.log(`[featureFlags] Override: ${key} = ${overrides[key]}`);
    }
  });
};

/**
 * Reseta todas as flags para seus valores default
 * Útil para testes
 */
export const resetFeatureFlags = () => {
  if (process.env.NODE_ENV === 'production') {
    console.warn('[featureFlags] resetFeatureFlags não deve ser usado em produção');
    return;
  }

  console.log('[featureFlags] Resetando todas as flags para valores default');
  // Forçar reimport do módulo (não funciona em todos os ambientes)
  // Na prática, recarregar a página é mais confiável
};

// ════════════════════════════════════════════════════════════════════════════
// INTEGRATION HOOKS (FUTURO)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Hook para integração com backend (placeholder)
 * 
 * Futuro: buscar flags de API remota
 * Ex: LaunchDarkly, ConfigCat, Firebase Remote Config
 */
export const fetchRemoteFlags = async () => {
  if (process.env.REACT_APP_FEATURE_FLAGS_API) {
    try {
      const response = await fetch(process.env.REACT_APP_FEATURE_FLAGS_API);
      const remoteFlags = await response.json();
      
      // Merge com flags locais (remote tem prioridade)
      Object.assign(FEATURE_FLAGS, remoteFlags);
      
      console.log('[featureFlags] Flags remotas carregadas:', Object.keys(remoteFlags).length);
    } catch (error) {
      console.error('[featureFlags] Erro ao buscar flags remotas:', error);
    }
  }
};

/**
 * Persiste preferências de feature flags no localStorage
 * Útil para testar features em staging
 */
export const loadLocalOverrides = () => {
  if (process.env.NODE_ENV === 'production') return;

  try {
    const saved = localStorage.getItem('arenastats_feature_flags_override');
    if (saved) {
      const overrides = JSON.parse(saved);
      setFeatureOverrides(overrides);
      console.log('[featureFlags] Overrides locais carregados:', Object.keys(overrides));
    }
  } catch (error) {
    console.error('[featureFlags] Erro ao carregar overrides locais:', error);
  }
};

/**
 * Salva overrides no localStorage
 */
export const saveLocalOverrides = (overrides) => {
  if (process.env.NODE_ENV === 'production') return;

  try {
    localStorage.setItem('arenastats_feature_flags_override', JSON.stringify(overrides));
    console.log('[featureFlags] Overrides salvos no localStorage');
  } catch (error) {
    console.error('[featureFlags] Erro ao salvar overrides:', error);
  }
};

// ════════════════════════════════════════════════════════════════════════════
// AUTO-INIT (Desenvolvimento)
// ════════════════════════════════════════════════════════════════════════════

if (process.env.NODE_ENV === 'development') {
  loadLocalOverrides();
  
  // Expor no window para debug no console
  if (typeof window !== 'undefined') {
    window.__FEATURE_FLAGS__ = FEATURE_FLAGS;
    window.__setFeatureOverrides__ = setFeatureOverrides;
    console.log('[featureFlags] Debug helpers expostos: window.__FEATURE_FLAGS__, window.__setFeatureOverrides__()');
  }
}

// ════════════════════════════════════════════════════════════════════════════
// EXPORTS
// ════════════════════════════════════════════════════════════════════════════

export default FEATURE_FLAGS;
